// src/components/files/FavoritesView.tsx
import React from 'react';
import {
  Box,
  Typography,
  Grid,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { useTranslation } from 'react-i18next';
import { useFileManager } from '../../contexts/FileManagerContext';
import FileItem from './FileItem';
import EmptyState from './EmptyState';

const FavoritesView: React.FC = () => {
  const { t } = useTranslation();
  const { state, navigateToFolder, selectItem, isSelected } = useFileManager();
  const { favorites, viewMode } = state;

  if (!favorites || (favorites.folders.length === 0 && favorites.documents.length === 0)) {
    return (
      <EmptyState
        icon={<StarIcon sx={{ fontSize: 80, color: 'grey.400' }} />}
        title={t('fileManager.favorites.empty', 'Aucun favori')}
        description={t('fileManager.favorites.emptyDescription', 'Ajoutez des dossiers ou fichiers aux favoris pour les retrouver ici')}
      />
    );
  }

  const handleFolderDoubleClick = (folderId: number) => {
    navigateToFolder(folderId);
  };

  const handleItemClick = (
    e: React.MouseEvent,
    id: number,
    type: 'folder' | 'document'
  ) => {
    selectItem({ id, type }, e.ctrlKey || e.metaKey, e.shiftKey);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Folders Section */}
      {favorites.folders.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, textTransform: 'uppercase' }}>
            {t('fileManager.favorites.folders', 'Dossiers favoris')} ({favorites.folders.length})
          </Typography>
          {viewMode === 'grid' ? (
            <Grid container spacing={2}>
              {favorites.folders.map((folder) => (
                <Grid item key={`folder-${folder.id}`}>
                  <FileItem
                    type="folder"
                    id={folder.id}
                    name={folder.name}
                    color={folder.color}
                    icon={folder.icon}
                    isFavorite={true}
                    createdAt={folder.created_at}
                    isSelected={isSelected(folder.id, 'folder')}
                    onClick={(e) => handleItemClick(e, folder.id, 'folder')}
                    onDoubleClick={() => handleFolderDoubleClick(folder.id)}
                    viewMode="grid"
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box>
              {favorites.folders.map((folder) => (
                <FileItem
                  key={`folder-${folder.id}`}
                  type="folder"
                  id={folder.id}
                  name={folder.name}
                  color={folder.color}
                  icon={folder.icon}
                  isFavorite={true}
                  createdAt={folder.created_at}
                  isSelected={isSelected(folder.id, 'folder')}
                  onClick={(e) => handleItemClick(e, folder.id, 'folder')}
                  onDoubleClick={() => handleFolderDoubleClick(folder.id)}
                  viewMode="list"
                />
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Documents Section */}
      {favorites.documents.length > 0 && (
        <Box>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, textTransform: 'uppercase' }}>
            {t('fileManager.favorites.files', 'Fichiers favoris')} ({favorites.documents.length})
          </Typography>
          {viewMode === 'grid' ? (
            <Grid container spacing={2}>
              {favorites.documents.map((doc) => (
                <Grid item key={`doc-${doc.id}`}>
                  <FileItem
                    type="document"
                    id={doc.id}
                    name={doc.title}
                    mimeType={doc.file_type}
                    fileSize={doc.file_size}
                    fileUrl={doc.file_url}
                    isFavorite={true}
                    createdAt={doc.created_at}
                    isSelected={isSelected(doc.id, 'document')}
                    onClick={(e) => handleItemClick(e, doc.id, 'document')}
                    viewMode="grid"
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box>
              {favorites.documents.map((doc) => (
                <FileItem
                  key={`doc-${doc.id}`}
                  type="document"
                  id={doc.id}
                  name={doc.title}
                  mimeType={doc.file_type}
                  fileSize={doc.file_size}
                  fileUrl={doc.file_url}
                  isFavorite={true}
                  createdAt={doc.created_at}
                  isSelected={isSelected(doc.id, 'document')}
                  onClick={(e) => handleItemClick(e, doc.id, 'document')}
                  viewMode="list"
                />
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default FavoritesView;
